import { Middleware } from 'redux'
import * as Sentry from '@sentry/browser'

import { getI18nPath } from 'features/i18n/router'
import { logEvent, setUserProperties, snowplowEvent } from 'lib/analytics'
import cookie, { clearAuthCookie } from 'lib/cookie'
import { ROUTE_PATH } from 'lib/paths'
import { getRedirectUrl } from 'lib/router'
import { SessionStorage } from 'lib/storage'
import { isSentryWatchedUser } from 'lib/helpers'

import {
  addActionToRetryQueueActionType,
  clearRefreshInProgress,
  loginWithEmailActionType,
  logoutActionType,
  refreshAccessTokenActionType,
  selectRetryQueue,
} from '../ducks/auth'

const authMiddleware: Middleware = (store) => (next) => (action) => {
  const currentUserId = cookie.userId?.get()
  const isWatchedUser = isSentryWatchedUser(currentUserId)

  // better Sentry error logging details for debugging auth-related errors
  if (action.type?.includes('auth/') || action.type?.includes('AUTH')) {
    if (isWatchedUser) {
      Sentry.addBreadcrumb({
        category: 'auth-flow',
        message: `Auth action: ${action.type}`,
        level: 'info',
        data: {
          actionType: action.type,
          hasError: !!action.error,
          refreshId: action.meta?.refreshId,
          retryCount: action.meta?.retryCount,
          timestamp: new Date().toISOString(),
          // don't log the token itself
          meta: action.meta ? { ...action.meta, token: undefined } : undefined,
        },
      })
    }
  }

  switch (action.type) {
    case logoutActionType.request:
      logEvent.account_log_out()
      break

    case logoutActionType.failure:
      if (isWatchedUser) {
        Sentry.addBreadcrumb({
          category: 'auth-flow',
          message: 'Logging out failure',
          level: 'warning',
        })
      }
    case logoutActionType.success:
      clearAuthCookie()
      snowplowEvent.logout()
      setUserProperties({})

      const target =
        window.opener && SessionStorage.authorizeWalletParams.get()
          ? ROUTE_PATH.AUTHORIZE_WALLET
          : ROUTE_PATH.HOME

      if (isWatchedUser) {
        Sentry.addBreadcrumb({
          category: 'auth-flow',
          message: 'Redirecting after logout',
          level: 'info',
          data: { target },
        })
      }

      // Traditional route change to reload application, allowing an app to be aware of a new cookies
      // We still need to pass i18n path because avoids Location get confused at the first load
      window.location.href = getI18nPath(target)
      break

    case refreshAccessTokenActionType.failure:
      // differentiate this from normal successful logout above for better error tracking
      // don't automatically logout; let error middleware handle this
      break

    case loginWithEmailActionType.success:
      const userId = action.payload.data.user_id || action.payload.data.id
      cookie.userId.set(userId)
      SessionStorage.snowplowTemp.set({ type: 'login', id: userId })

      const redirectUrl = window.decodeURIComponent(
        action?.meta?.previousAction?.meta?.redirectUrl ??
          getRedirectUrl(window.location.href) ??
          window.location.pathname // Stay in the same page with login modal closed, e.g. in case a user logs in from a group page.
      )

      if (isWatchedUser) {
        Sentry.addBreadcrumb({
          category: 'auth-flow',
          message: 'Login successful, redirecting',
          level: 'info',
          data: { redirectUrl },
        })
      }

      // Traditional route change to reload application, allowing an app to be aware of a new cookies.
      window.location.href = redirectUrl
      break

    // when new token load success
    case refreshAccessTokenActionType.success:
      if (isWatchedUser) {
        Sentry.addBreadcrumb({
          category: 'auth-flow',
          message: 'Successful token refresh',
          level: 'info',
          data: {
            refreshId: action.meta?.refreshId,
            wasRetry: action.meta?.retryCount > 0,
            queueLength: selectRetryQueue(store.getState()).length,
            timestamp: new Date().toISOString(),
          },
        })
      }

      // clear in-progress state if it's still set
      if (action.meta?.refreshId) {
        store.dispatch(clearRefreshInProgress(action.meta.refreshId))
      }

      // replay action in queue
      selectRetryQueue(store.getState()).map((action: any) => {
        store.dispatch(action)
        store.dispatch({
          type: addActionToRetryQueueActionType.pop,
        })
      })
      break
  }

  return next(action)
}

export default authMiddleware
